import { DialogRef } from "@angular/cdk/dialog"
import { Component, DestroyRef, effect, inject, input, signal } from "@angular/core"
import { takeUntilDestroyed } from "@angular/core/rxjs-interop"
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from "@angular/forms"
import { firstValueFrom } from "rxjs"

import { Station, StoreInfo, Ticket } from "src/app/_models"
import { ModalService, TicketService } from "src/app/_services"

type StationFormGroup = FormGroup<{
  key: FormControl<string>
  title: FormControl<string>
  tausche_kasse: FormControl<boolean>
  tausche_drucker: FormControl<boolean>
  tausche_monitor: FormControl<boolean>
}>

interface StationFormValue {
  key: string
  title: string
  tausche_kasse: boolean
  tausche_drucker: boolean
  tausche_monitor: boolean
}

@Component({
  standalone: true,
  selector: "hawk-store-info-editor",
  templateUrl: "./store-info-editor.component.html",
  imports: [FormsModule, ReactiveFormsModule],
  host: {
    class: "flex flex-col gap-4 p-4",
  },
})
export class StoreInfoEditorComponent {
  // Static-Open-Pattern wie BatchEditFormComponent — Modal-Trigger über ModalService.
  public static open(modal: ModalService, options: { ticket: Ticket }) {
    return modal.open<StoreInfoEditorComponent, Ticket>(StoreInfoEditorComponent, {
      inputs: options,
    })
  }

  private ticketService = inject(TicketService)
  private fb = inject(FormBuilder)
  private dialogRef = inject(DialogRef<Ticket, StoreInfoEditorComponent>)
  private destroyRef = inject(DestroyRef)

  // Erlaubte Zeichen im Station-Key (jsonb-Schlüssel in tickets.store_info.stations).
  protected static readonly KEY_PATTERN = /^[a-z0-9_]+$/

  public ticket = input.required<Ticket>()

  protected stations: FormArray<StationFormGroup> = this.fb.array<StationFormGroup>([])

  protected isSaving = signal(false)
  protected errorMessage = signal<string | null>(null)

  public constructor() {
    // Beim ersten Setzen des Ticket-Inputs Stationen aus store_info initialisieren.
    let initialized = false
    effect(() => {
      const ticket = this.ticket()
      if (initialized || !ticket) return
      this.populateFromTicket(ticket)
      initialized = true
    })
  }

  // -------- Initialisierung ----------------------------------------------------

  private populateFromTicket(ticket: Ticket): void {
    const stationsHash = ticket.store_info?.stations ?? {}
    while (this.stations.length > 0) {
      this.stations.removeAt(0)
    }
    for (const key of Object.keys(stationsHash)) {
      const station = stationsHash[key]
      this.stations.push(this.buildStationGroup(key, station))
    }
  }

  private buildStationGroup(key: string, station: Station): StationFormGroup {
    const group = this.fb.group({
      key: this.fb.nonNullable.control<string>(key),
      title: this.fb.nonNullable.control<string>(station?.title ?? ""),
      tausche_kasse: this.fb.nonNullable.control<boolean>(!!station?.tausche_kasse),
      tausche_drucker: this.fb.nonNullable.control<boolean>(!!station?.tausche_drucker),
      tausche_monitor: this.fb.nonNullable.control<boolean>(!!station?.tausche_monitor),
    }) as StationFormGroup

    // Title-Eingabe füllt den Key vor — solange der User den Key nicht selbst editiert hat.
    // Sobald keyControl.dirty=true ist (User hat Key getippt), stoppt die Sync.
    group.controls.title.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((title) => {
        if (!group.controls.key.dirty) {
          group.controls.key.setValue(this.slugify(title), { emitEvent: false })
        }
      })

    return group
  }

  // Wandelt einen Titel in einen gültigen Station-Key: lowercase, nur [a-z0-9_].
  // Beispiele: "Kasse 7" → "kasse7", "Bedienwaage-A" → "bedienwaagea".
  private slugify(input: string): string {
    return (input ?? "").toLowerCase().replace(/[^a-z0-9_]/g, "")
  }

  // -------- Stations-Verwaltung ------------------------------------------------

  protected addStation(): void {
    this.stations.push(
      this.buildStationGroup("", {
        title: "",
        tausche_kasse: false,
        tausche_drucker: false,
        tausche_monitor: false,
      }),
    )
  }

  protected removeStation(index: number): void {
    if (index < 0 || index >= this.stations.length) return
    this.stations.removeAt(index)
  }

  protected get isEmpty(): boolean {
    return this.stations.length === 0
  }

  // -------- Serialisierung -----------------------------------------------------

  protected serialize(): StoreInfo {
    const values = this.stations.controls.map((control) => control.getRawValue() as StationFormValue)
    const stations: Record<string, Station> = {}
    for (const value of values) {
      stations[value.key] = {
        title: (value.title ?? "").trim(),
        tausche_kasse: !!value.tausche_kasse,
        tausche_drucker: !!value.tausche_drucker,
        tausche_monitor: !!value.tausche_monitor,
      }
    }
    return { stations }
  }

  // -------- Save / Cancel ------------------------------------------------------

  protected handleCancel(): void {
    this.dialogRef.close()
  }

  protected async handleSave(): Promise<void> {
    if (this.isSaving()) return
    this.errorMessage.set(null)

    const validationError = this.validateKeys()
    if (validationError) {
      this.errorMessage.set(validationError)
      return
    }

    this.isSaving.set(true)

    const ticketId = String(this.ticket().id)
    const payload = this.serialize()

    try {
      const updated = await firstValueFrom(this.ticketService.updateStoreInfo(ticketId, payload))
      this.dialogRef.close(updated)
    } catch (error: unknown) {
      this.errorMessage.set(this.extractErrorMessage(error))
    } finally {
      this.isSaving.set(false)
    }
  }

  // Prüft alle Station-Keys: nicht leer, Pattern [a-z0-9_]+, unique.
  // Liefert eine Fehlermeldung oder null, wenn alles in Ordnung ist.
  private validateKeys(): string | null {
    const keys = this.stations.controls.map((control) => control.getRawValue().key)
    if (keys.some((key) => !key)) {
      return 'Jede Station braucht einen Key (z. B. "kasse7").'
    }
    const invalid = keys.find((key) => !StoreInfoEditorComponent.KEY_PATTERN.test(key))
    if (invalid !== undefined) {
      return `Ungültiger Key "${invalid}". Erlaubt sind nur a–z, 0–9 und _.`
    }
    const duplicates = keys.filter((key, index) => keys.indexOf(key) !== index)
    if (duplicates.length > 0) {
      return `Doppelter Key "${duplicates[0]}". Jeder Key darf nur einmal vorkommen.`
    }
    return null
  }

  private extractErrorMessage(error: unknown): string {
    const httpError = error as { status?: number; error?: { errors?: { store_info?: string[] } } }
    const storeInfoErrors = httpError?.error?.errors?.store_info
    if (Array.isArray(storeInfoErrors) && storeInfoErrors.length > 0) {
      return storeInfoErrors[0]
    }
    if (httpError?.status === 403) {
      return "Keine Berechtigung, die Filialinfo zu ändern."
    }
    return "Speichern fehlgeschlagen. Bitte erneut versuchen."
  }
}
